import { supabase } from '../lib/supabase'
import { supabaseError } from '../lib/errors'
import { diffLabelAssignments } from '../lib/labelAssignments'
import type { InboxItemType } from '../types/inbox'

export interface InboxLabelAssignmentRow {
  entity_type: InboxItemType
  entity_id: string
  label_id: string
}

export async function fetchInboxLabelAssignments(): Promise<InboxLabelAssignmentRow[]> {
  const { data, error } = await supabase
    .from('inbox_label_assignments')
    .select('entity_type, entity_id, label_id')
  if (error) throw supabaseError(error, 'Failed to load inbox labels')
  return (data || []) as InboxLabelAssignmentRow[]
}

export function groupInboxLabelsByEntity(
  rows: InboxLabelAssignmentRow[]
): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const row of rows) {
    const key = `${row.entity_type}:${row.entity_id}`
    out[key] = [...(out[key] || []), row.label_id]
  }
  return out
}

export async function addInboxLabel(
  entityType: InboxItemType,
  entityId: string,
  labelId: string
): Promise<void> {
  const { error } = await supabase
    .from('inbox_label_assignments')
    .insert({ entity_type: entityType, entity_id: entityId, label_id: labelId })
  if (error && error.code !== '23505') throw supabaseError(error, 'Failed to add inbox label')

  if (entityType === 'conversation') {
    const { error: legacyErr } = await supabase
      .from('conversation_label_assignments')
      .insert({ conversation_id: entityId, label_id: labelId })
    if (legacyErr && legacyErr.code !== '23505') {
      throw supabaseError(legacyErr, 'Failed to add conversation label')
    }
  }
}

export async function removeInboxLabel(
  entityType: InboxItemType,
  entityId: string,
  labelId: string
): Promise<void> {
  const { error } = await supabase
    .from('inbox_label_assignments')
    .delete()
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .eq('label_id', labelId)
  if (error) throw supabaseError(error, 'Failed to remove inbox label')

  if (entityType === 'conversation') {
    await supabase
      .from('conversation_label_assignments')
      .delete()
      .eq('conversation_id', entityId)
      .eq('label_id', labelId)
  }
}

export async function setInboxLabels(
  entityType: InboxItemType,
  entityId: string,
  currentLabelIds: string[],
  nextLabelIds: string[]
): Promise<void> {
  const { toAdd, toRemove } = diffLabelAssignments(currentLabelIds, nextLabelIds)
  for (const labelId of toAdd) await addInboxLabel(entityType, entityId, labelId)
  for (const labelId of toRemove) await removeInboxLabel(entityType, entityId, labelId)
}

export async function setPhoneCallLogState(
  id: string,
  state: 'active' | 'archived' | 'spam' | 'deleted'
): Promise<void> {
  const { error } = await supabase
    .from('phone_call_logs')
    .update({ state, state_changed_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw supabaseError(error, 'Failed to update call state')
}
